/**
 * Native Performance Service v1.0
 * 
 * Ultra-optimized performance layer for 60fps smooth experience
 * Specifically designed for Capacitor APK builds
 * 
 * Features:
 * - Hardware acceleration
 * - Animation frame optimization
 * - Memory management
 * - Scroll optimization
 * - Touch response optimization
 * - GPU layer promotion
 */

import { Capacitor } from '@capacitor/core';
import { requestIdleCallback } from '../utils/performanceHelpers';

// Performance configuration
interface PerformanceConfig {
    enableHardwareAcceleration: boolean;
    enableGPUCompositing: boolean;
    targetFPS: number;
    enablePassiveListeners: boolean;
    enableWillChange: boolean;
    enableContentVisibility: boolean;
    enableVirtualization: boolean;
    scrollOptimization: boolean;
    touchOptimization: boolean;
    memoryManagement: boolean;
    reduceMotion: boolean;
}

const DEFAULT_PERFORMANCE_CONFIG: PerformanceConfig = {
    enableHardwareAcceleration: true,
    enableGPUCompositing: true,
    targetFPS: 60,
    enablePassiveListeners: true,
    enableWillChange: true,
    enableContentVisibility: true,
    enableVirtualization: true,
    scrollOptimization: true,
    touchOptimization: true,
    memoryManagement: true,
    reduceMotion: false,
};

class NativePerformanceService {
    private config: PerformanceConfig = DEFAULT_PERFORMANCE_CONFIG;
    private isNative: boolean = false;
    private frameCallbacks: Set<(timestamp: number) => void> = new Set();
    private animationFrameId: number | null = null;
    private lastFrameTime: number = 0;
    private frameInterval: number = 1000 / 60; // 16.67ms for 60fps
    private memoryWarningThreshold: number = 0.8;
    private intersectionObserver: IntersectionObserver | null = null;
    private resizeObserver: ResizeObserver | null = null;

    constructor() {
        this.isNative = Capacitor.isNativePlatform();
        this.initialize();
    }

    private initialize(): void {
        if (typeof window === 'undefined') return;

        // Apply performance optimizations
        this.applyGlobalOptimizations();
        this.setupScrollOptimization();
        this.setupTouchOptimization();
        this.setupMemoryManagement();
        this.setupVisibilityOptimization();
        this.setupAnimationLoop();

        console.log('[Performance] Service initialized', {
            isNative: this.isNative,
            platform: Capacitor.getPlatform(),
        });
    }

    /**
     * Apply global CSS optimizations for 60fps
     */
    private applyGlobalOptimizations(): void {
        const style = document.createElement('style');
        style.id = 'native-performance-optimizations';
        style.textContent = `
            /* GPU Layer Promotion for Smooth Animations */
            .gpu-accelerated,
            .animate-fade-in,
            .animate-slide-up,
            .animate-slide-down,
            .animate-scale-in,
            .animate-bounce,
            [class*="animate-"] {
                transform: translateZ(0);
                backface-visibility: hidden;
                perspective: 1000px;
                will-change: transform, opacity;
            }

            /* Optimized Scrolling */
            .scroll-smooth,
            .overflow-y-auto,
            .overflow-x-auto,
            .overflow-auto {
                -webkit-overflow-scrolling: touch;
                scroll-behavior: smooth;
                overscroll-behavior: contain;
            }

            /* Touch Optimization */
            * {
                -webkit-tap-highlight-color: transparent;
                touch-action: manipulation;
            }

            button, a, [role="button"] {
                touch-action: manipulation;
                -webkit-user-select: none;
                user-select: none;
            }

            /* Image Optimization */
            img {
                content-visibility: auto;
                contain-intrinsic-size: auto 300px;
            }

            /* List Virtualization Support */
            .virtualized-list {
                contain: strict;
                content-visibility: auto;
            }

            /* Reduce Layout Thrashing */
            .layout-stable {
                contain: layout style paint;
            }

            /* Hardware Accelerated Transitions */
            .hw-accelerated-transition {
                transition-property: transform, opacity;
                transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
                transform: translateZ(0);
            }

            /* Optimized Modal/Dialog Animations */
            .modal-optimized {
                transform: translate3d(0, 0, 0);
                will-change: transform, opacity;
                backface-visibility: hidden;
            }

            /* Scroll Container Optimization */
            .scroll-container-optimized {
                transform: translateZ(0);
                will-change: scroll-position;
                contain: strict;
                overflow-anchor: auto;
            }

            /* Input Optimization */
            input, textarea, select {
                font-size: 16px !important; /* Prevent iOS zoom */
            }

            /* Reduce Motion for Performance */
            @media (prefers-reduced-motion: reduce) {
                *, *::before, *::after {
                    animation-duration: 0.01ms !important;
                    animation-iteration-count: 1 !important;
                    transition-duration: 0.01ms !important;
                }
            }

            /* Native Platform Specific Optimizations */
            ${this.isNative ? `
                /* Extra native optimizations */
                body {
                    -webkit-touch-callout: none;
                    -webkit-user-select: none;
                    user-select: none;
                    overscroll-behavior: none;
                }

                /* Prevent pull-to-refresh */
                html, body {
                    overscroll-behavior-y: contain;
                }

                /* Faster touch response */
                * {
                    touch-action: pan-x pan-y;
                }

                /* Safe area handling */
                .safe-area-top {
                    padding-top: env(safe-area-inset-top, 0);
                }
                .safe-area-bottom {
                    padding-bottom: env(safe-area-inset-bottom, 0);
                }
            ` : ''}

            /* 60fps Animation Keyframes */
            @keyframes fadeIn60fps {
                from { opacity: 0; transform: translate3d(0, 0, 0); }
                to { opacity: 1; transform: translate3d(0, 0, 0); }
            }

            @keyframes slideUp60fps {
                from { opacity: 0; transform: translate3d(0, 20px, 0); }
                to { opacity: 1; transform: translate3d(0, 0, 0); }
            }

            @keyframes slideDown60fps {
                from { opacity: 0; transform: translate3d(0, -20px, 0); }
                to { opacity: 1; transform: translate3d(0, 0, 0); }
            }

            @keyframes scaleIn60fps {
                from { opacity: 0; transform: scale3d(0.95, 0.95, 1); }
                to { opacity: 1; transform: scale3d(1, 1, 1); }
            }

            @keyframes pulse60fps {
                0%, 100% { opacity: 1; transform: scale3d(1, 1, 1); }
                50% { opacity: 0.7; transform: scale3d(1.02, 1.02, 1); }
            }

            /* Buttery Smooth Animations */
            .animate-fade-in-60fps {
                animation: fadeIn60fps 0.2s cubic-bezier(0.4, 0, 0.2, 1) forwards;
            }

            .animate-slide-up-60fps {
                animation: slideUp60fps 0.25s cubic-bezier(0.4, 0, 0.2, 1) forwards;
            }

            .animate-slide-down-60fps {
                animation: slideDown60fps 0.25s cubic-bezier(0.4, 0, 0.2, 1) forwards;
            }

            .animate-scale-in-60fps {
                animation: scaleIn60fps 0.2s cubic-bezier(0.4, 0, 0.2, 1) forwards;
            }

            .animate-pulse-60fps {
                animation: pulse60fps 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
            }
        `;

        // Remove existing if present
        const existing = document.getElementById('native-performance-optimizations');
        if (existing) existing.remove();

        document.head.appendChild(style);
    }

    /**
     * Optimized scroll handling
     */
    private setupScrollOptimization(): void {
        if (!this.config.scrollOptimization) return;

        // Use passive listeners for scroll
        const scrollHandler = this.throttle(() => {
            // Trigger any scroll-based optimizations
            this.onScroll();
        }, 16); // ~60fps

        document.addEventListener('scroll', scrollHandler, { passive: true, capture: true });

        // Intersection Observer for lazy loading
        this.intersectionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                        (entry.target as HTMLElement).style.contentVisibility = 'visible';
                    } else {
                        entry.target.classList.remove('is-visible');
                        (entry.target as HTMLElement).style.contentVisibility = 'auto';
                    }
                });
            },
            {
                rootMargin: '50px',
                threshold: 0.01
            }
        );
    }

    /**
     * Touch response optimization
     */
    private setupTouchOptimization(): void {
        if (!this.config.touchOptimization) return;

        // Prevent 300ms delay on touch
        document.addEventListener('touchstart', () => { }, { passive: true });

        // Fast active states
        document.addEventListener('touchstart', (e) => {
            const target = e.target as HTMLElement;
            if (target.matches('button, a, [role="button"], .touchable')) {
                target.classList.add('touch-active');
            }
        }, { passive: true });

        document.addEventListener('touchend', () => {
            document.querySelectorAll('.touch-active').forEach(el => {
                el.classList.remove('touch-active');
            });
        }, { passive: true });
    }

    /**
     * Memory management for smooth performance
     */
    private setupMemoryManagement(): void {
        if (!this.config.memoryManagement) return;

        // Monitor memory usage
        if ('memory' in performance) {
            setInterval(() => {
                const memory = (performance as any).memory;
                const usageRatio = memory.usedJSHeapSize / memory.jsHeapSizeLimit;

                if (usageRatio > this.memoryWarningThreshold) {
                    this.triggerGarbageCollection();
                }
            }, 30000); // Check every 30 seconds
        }

        // Clear unused images from memory
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.releaseUnusedResources();
            }
        });
    }

    /**
     * Content visibility optimization
     */
    private setupVisibilityOptimization(): void {
        if (!this.config.enableContentVisibility) return;

        // Apply content-visibility to heavy elements
        requestIdleCallback(() => {
            document.querySelectorAll('.message-item, .list-item, .card').forEach(el => {
                (el as HTMLElement).style.contentVisibility = 'auto';
                (el as HTMLElement).style.containIntrinsicSize = 'auto 100px';
            });
        });
    }

    /**
     * High-performance animation loop
     */
    private setupAnimationLoop(): void {
        const loop = (timestamp: number) => {
            const deltaTime = timestamp - this.lastFrameTime;

            // Maintain 60fps
            if (deltaTime >= this.frameInterval) {
                this.lastFrameTime = timestamp - (deltaTime % this.frameInterval);

                // Execute frame callbacks
                this.frameCallbacks.forEach(callback => {
                    try {
                        callback(timestamp);
                    } catch (e) {
                        console.error('[Performance] Frame callback error:', e);
                    }
                });
            }

            this.animationFrameId = requestAnimationFrame(loop);
        };

        this.animationFrameId = requestAnimationFrame(loop);
    }

    /**
     * Register a callback to run every frame (60fps)
     */
    public onFrame(callback: (timestamp: number) => void): () => void {
        this.frameCallbacks.add(callback);
        return () => this.frameCallbacks.delete(callback);
    }

    /**
     * Optimize an element for GPU compositing
     */
    public optimizeElement(element: HTMLElement): void {
        element.style.transform = 'translateZ(0)';
        element.style.backfaceVisibility = 'hidden';
        element.style.willChange = 'transform, opacity';
        element.classList.add('gpu-accelerated');
    }

    /**
     * Observe element for lazy loading
     */
    public observeElement(element: HTMLElement): void {
        if (this.intersectionObserver) {
            this.intersectionObserver.observe(element);
        }
    }

    /**
     * Unobserve element
     */
    public unobserveElement(element: HTMLElement): void {
        if (this.intersectionObserver) {
            this.intersectionObserver.unobserve(element);
        }
    }

    /**
     * Request animation with optimal timing
     */
    public requestOptimalAnimation(callback: () => void): number {
        return requestAnimationFrame(() => {
            // Use double RAF for more consistent timing
            requestAnimationFrame(callback);
        });
    }

    /**
     * Batch DOM reads and writes for optimal performance
     */
    public batchDOMOperations(
        reads: (() => void)[],
        writes: (() => void)[]
    ): void {
        // Execute all reads first
        requestAnimationFrame(() => {
            reads.forEach(read => read());

            // Then execute all writes in the next frame
            requestAnimationFrame(() => {
                writes.forEach(write => write());
            });
        });
    }

    /**
     * Smooth scroll to element
     */
    public smoothScrollTo(element: HTMLElement, offset: number = 0): void {
        const targetPosition = element.getBoundingClientRect().top + window.scrollY - offset;

        window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
        });
    }

    /**
     * Throttle function for scroll handlers
     */
    private throttle<T extends (...args: any[]) => void>(
        func: T,
        limit: number
    ): T {
        let inThrottle: boolean;
        return ((...args: any[]) => {
            if (!inThrottle) {
                func.apply(this, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        }) as T;
    }

    /**
     * Debounce function for resize handlers
     */
    public debounce<T extends (...args: any[]) => void>(
        func: T,
        wait: number
    ): T {
        let timeout: NodeJS.Timeout;
        return ((...args: any[]) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        }) as T;
    }

    /**
     * Trigger garbage collection if available
     */
    private triggerGarbageCollection(): void {
        // Clear unused references
        if (typeof window !== 'undefined') {
            // Force cleanup of detached DOM nodes
            const temp = document.createElement('div');
            document.body.appendChild(temp);
            document.body.removeChild(temp);
        }

        console.log('[Performance] Memory cleanup triggered');
    }

    /**
     * Release unused resources
     */
    private releaseUnusedResources(): void {
        // Clear image caches when app is in background
        document.querySelectorAll('img[data-lazy]').forEach(img => {
            if (!img.classList.contains('is-visible')) {
                (img as HTMLImageElement).src = '';
            }
        });
    }

    /**
     * Handle scroll events
     */
    private onScroll(): void {
        // Dispatch custom event for components that need scroll info
        window.dispatchEvent(new CustomEvent('optimized-scroll'));
    }

    /**
     * Get current FPS
     */
    public getCurrentFPS(): number {
        const now = performance.now();
        const fps = 1000 / (now - this.lastFrameTime);
        return Math.round(fps);
    }

    /**
     * Check if running on native platform
     */
    public isNativePlatform(): boolean {
        return this.isNative;
    }

    /**
     * Get platform info
     */
    public getPlatformInfo(): { platform: string; isNative: boolean } {
        return {
            platform: Capacitor.getPlatform(),
            isNative: this.isNative
        };
    }

    /**
     * Apply will-change hint before animation
     */
    public prepareAnimation(element: HTMLElement, properties: string[]): void {
        element.style.willChange = properties.join(', ');

        // Remove will-change after animation completes
        element.addEventListener('animationend', () => {
            element.style.willChange = 'auto';
        }, { once: true });

        element.addEventListener('transitionend', () => {
            element.style.willChange = 'auto';
        }, { once: true });
    }

    /**
     * Cleanup
     */
    public destroy(): void {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
        }

        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }

        this.frameCallbacks.clear();
    }
}

// Singleton instance
export const performanceService = new NativePerformanceService();

// Export helper functions
export const optimizeElement = (el: HTMLElement) => performanceService.optimizeElement(el);
export const observeElement = (el: HTMLElement) => performanceService.observeElement(el);
export const requestOptimalAnimation = (cb: () => void) => performanceService.requestOptimalAnimation(cb);
export const onFrame = (cb: (ts: number) => void) => performanceService.onFrame(cb);
export const isNativePlatform = () => performanceService.isNativePlatform();
export const getPlatformInfo = () => performanceService.getPlatformInfo();
export const debounce = performanceService.debounce;

export default performanceService;

